import {
  cart,
  isInsideTriangle,
  TriangulatedPoint,
} from '@asset-viz/utils/fastMath'
import { convertCoordsToUVs } from '@asset-viz/utils/helpers'
import { useMemo } from 'react'
import { BufferAttribute, InterleavedBufferAttribute, Vector3 } from 'three'
import { ComponentSurfaceWrappingMode, GeckoComponentCoords } from 'types'

import { useSurfaceProps } from './SurfaceProvider'

/**
 * Takes in a mesh and a point to draw on the mesh,  and returns a point
 * that falls within the  bounds of the mesh
 * @param point The point in Gecko 2D component coordinates
 * @returns point as an array
 */
export const useSurfaceSample = (
  point: GeckoComponentCoords | undefined
):
  | {
      position: Vector3
      normal: Vector3
      boundaryPosition: Vector3
      boundaryNormal: Vector3
    }
  | {
      position: Vector3
      normal: Vector3
      boundaryPosition: undefined
      boundaryNormal: undefined
    }
  | {
      position: undefined
      normal: undefined
      boundaryPosition: undefined
      boundaryNormal: undefined
    } => {
  const { geometry, bounds, wrapMode } = useSurfaceProps()

  return useMemo(() => {
    const inputUVs = geometry.getAttribute('uv')
    if (!inputUVs) throw new Error('No "uv" attribute!')

    const inputPositions = geometry.getAttribute('position')
    if (!inputPositions) throw new Error('No "position" attribute!')

    const inputNormals = geometry.getAttribute('normal')
    if (!inputPositions) throw new Error('No "normal" attribute!')

    const pointCount = inputUVs.array.length / 2
    if (pointCount !== inputPositions.array.length / 3)
      throw new Error(
        `'uv' Vec2 count (${pointCount}) does not match ` +
          `'position' Vec3 count (${inputPositions.array.length / 3})!`
      )
    if (pointCount !== inputNormals.array.length / 3)
      throw new Error(
        `'uv' Vec2 count (${pointCount}) does not match ` +
          `'normal' Vec3 count (${inputNormals.array.length / 3})!`
      )

    if (!point || point.componentID !== bounds.componentID)
      return {
        position: undefined,
        normal: undefined,
        boundaryPosition: undefined,
        boundaryNormal: undefined,
      }

    const [sx, sy] = convertCoordsToUVs(point, bounds)
    if (sx < 0 || sy < 0 || sx > 1 || sy > 1)
      return {
        position: undefined,
        normal: undefined,
        boundaryPosition: undefined,
        boundaryNormal: undefined,
      }

    let bx = undefined
    let by = undefined
    if (
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY &&
      (sy === 0 || sy === 1)
    ) {
      by = sy === 0 ? 1 : 0
      bx = sx
    } else if (
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX &&
      (sx === 0 || sx === 1)
    ) {
      bx = sx === 0 ? 1 : 0
      by = sy
    }

    const indexAttribute = geometry.getIndex()
    // Many imported meshes (including glTF) keep their triangles indexed, so account for that shape
    const indexArray = indexAttribute ? indexAttribute.array : undefined
    const triangleCount = indexAttribute
      ? indexAttribute.count / 3
      : pointCount / 3

    let sample = undefined
    let boundarySample = undefined

    function arrayIndexer(index: number) {
      return indexArray ? Number(indexArray[index]) : index
    }

    for (let t = 0; t < triangleCount; t++) {
      const baseIndex = t * 3
      const ia = arrayIndexer(baseIndex)
      const ib = arrayIndexer(baseIndex + 1)
      const ic = arrayIndexer(baseIndex + 2)

      const au = inputUVs.array[ia * 2]
      const av = inputUVs.array[ia * 2 + 1]

      const bu = inputUVs.array[ib * 2]
      const bv = inputUVs.array[ib * 2 + 1]

      const cu = inputUVs.array[ic * 2]
      const cv = inputUVs.array[ic * 2 + 1]

      const maxU = Math.max(au, bu, cu)
      const maxV = Math.max(av, bv, cv)
      const minU = Math.min(au, bu, cu)
      const minV = Math.min(av, bv, cv)

      if (
        // check if triangle and line segment have any overlap
        !(sx <= maxU && sx >= minU && sy <= maxV && sy >= minV) &&
        (bx === undefined ||
          by === undefined ||
          !(bx <= maxU && bx >= minU && by <= maxV && by >= minV))
      )
        continue

      const point = cart(sx, sy, au, av, bu, bv, cu, cv)
      if (isInsideTriangle(point)) {
        sample = calculateMeshPositionAndNormalFromTriangle(
          point,
          ia,
          ib,
          ic,
          inputPositions,
          inputNormals
        )

        if (bx === undefined)
          return { normal: sample.normal, position: sample.position }
        else if (boundarySample !== undefined)
          return {
            normal: sample.normal,
            position: sample.position,
            boundaryPosition: boundarySample.position,
            boundaryNormal: boundarySample.normal,
          }
      }
      if (bx !== undefined && by !== undefined) {
        const boundaryPoint = cart(bx, by, au, av, bu, bv, cu, cv)
        if (isInsideTriangle(boundaryPoint)) {
          boundarySample = calculateMeshPositionAndNormalFromTriangle(
            boundaryPoint,
            ia,
            ib,
            ic,
            inputPositions,
            inputNormals
          )
          if (sample !== undefined)
            return {
              normal: sample.normal,
              position: sample.position,
              boundaryPosition: boundarySample.position,
              boundaryNormal: boundarySample.normal,
            }
        }
      }
    }

    if (sample === undefined) {
      return {
        position: undefined,
        normal: undefined,
        boundaryPosition: undefined,
        boundaryNormal: undefined,
      }
    } else {
      return { position: sample.position, normal: sample.normal }
    }
  }, [point, geometry, bounds, wrapMode])
}

const calculateMeshPositionAndNormalFromTriangle = (
  point: TriangulatedPoint,
  tpa: number,
  tpb: number,
  tpc: number,
  inputPositions: BufferAttribute | InterleavedBufferAttribute,
  inputNormals: BufferAttribute | InterleavedBufferAttribute
) => {
  const pax = inputPositions.array[tpa * 3]
  const pay = inputPositions.array[tpa * 3 + 1]
  const paz = inputPositions.array[tpa * 3 + 2]

  const pbx = inputPositions.array[tpb * 3]
  const pby = inputPositions.array[tpb * 3 + 1]
  const pbz = inputPositions.array[tpb * 3 + 2]

  const pcx = inputPositions.array[tpc * 3]
  const pcy = inputPositions.array[tpc * 3 + 1]
  const pcz = inputPositions.array[tpc * 3 + 2]

  const nax = inputNormals.array[tpa * 3]
  const nay = inputNormals.array[tpa * 3 + 1]
  const naz = inputNormals.array[tpa * 3 + 2]

  const nbx = inputNormals.array[tpb * 3]
  const nby = inputNormals.array[tpb * 3 + 1]
  const nbz = inputNormals.array[tpb * 3 + 2]

  const ncx = inputNormals.array[tpc * 3]
  const ncy = inputNormals.array[tpc * 3 + 1]
  const ncz = inputNormals.array[tpc * 3 + 2]

  return {
    position: new Vector3(
      point.a * pax + point.b * pbx + point.c * pcx,
      point.a * pay + point.b * pby + point.c * pcy,
      point.a * paz + point.b * pbz + point.c * pcz
    ),
    normal: new Vector3(
      point.a * nax + point.b * nbx + point.c * ncx,
      point.a * nay + point.b * nby + point.c * ncy,
      point.a * naz + point.b * nbz + point.c * ncz
    ),
  }
}
